import React from "react";
import { PersonChip } from "@ui/person-chip";
import { TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Search } from "@material-ui/icons";
import { differenceBy } from "lodash";

import { Autocomplete } from "@material-ui/lab";

import { styles } from "./person-selector.styles";
import { Person } from "./types";
import { PersonSuggestion } from "./person-suggestion";
import classNames from "classnames";

const useStyles = makeStyles(styles);

export type Props<T> = {
  onAdd: (person: T) => void;
  onDelete: (person: T) => void;
  onSearch: (query: string) => void;
  suggestions: T[];
  selections: T[];
  placeholder?: string;
  fullWidth?: boolean;
};

export const PersonSelector = <T extends Person>({
  onAdd,
  onDelete,
  onSearch,
  suggestions,
  selections,
  placeholder = "Search",
  fullWidth = false,
}: Props<T>) => {
  const classes = useStyles();

  return (
    <Autocomplete
      multiple
      disableClearable
      filterSelectedOptions
      autoHighlight
      options={suggestions}
      getOptionLabel={(option) => {
        return option.email.toString();
      }}
      value={selections}
      onChange={(_e, value: (T | undefined)[]) => {
        const sanitizedValues = value.filter(<T extends unknown>(v: T): v is NonNullable<T> => !!v);
        const added = differenceBy(sanitizedValues, selections, ({ email }) => email);
        const removed = differenceBy(selections, sanitizedValues, ({ email }) => email);
        added.forEach((p) => onAdd(p));
        removed.forEach((p) => onDelete(p));
      }}
      onInputChange={(_e, value) => onSearch(value)}
      renderTags={(value: T[], getTagProps) =>
        value.map((person, index) => {
          // getTagProps returns an object with the following props:
          // key, data-tag-index, tabIndex, onDelete, className, disabled
          const tagProps = getTagProps({ index });
          return (
            <PersonChip
              email={person.email}
              name={person.name}
              imageUrl={person.externalImageUrl}
              {...tagProps}
            />
          );
        })
      }
      renderInput={({ InputProps, inputProps, ...otherProps }) => {
        return (
          <TextField
            {...otherProps}
            classes={{ root: classes.searchField }}
            placeholder={selections.length === 0 ? placeholder : ""}
            fullWidth={fullWidth}
            inputProps={{
              ...inputProps,
            }}
            InputProps={{
              ...InputProps,
              className: classNames(InputProps.className, classes.searchInput),
              disableUnderline: true,
              startAdornment: [
                <Search key="input-search-icon" className={classes.searchIcon} />,
                ...React.Children.toArray(InputProps.startAdornment),
              ],
              endAdornment: null,
            }}
          />
        );
      }}
      renderOption={(person: T) => <PersonSuggestion person={person} />}
      popupIcon={null}
    />
  );
};
